import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  X,
  XCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { IKbSyncResult, TKbSyncTone } from '@/types/knowledgeBase.types';
import { cn } from '@/utils';
import { kbSyncStatusTone } from '@/utils/knowledgeBase';

const TONE_STYLES: Record<
  TKbSyncTone,
  { icon: typeof CheckCircle2; row: string; text: string }
> = {
  success: {
    icon: CheckCircle2,
    row: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40',
    text: 'text-emerald-700 dark:text-emerald-400',
  },
  warning: {
    icon: AlertTriangle,
    row: 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40',
    text: 'text-amber-700 dark:text-amber-400',
  },
  error: {
    icon: XCircle,
    row: 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40',
    text: 'text-red-700 dark:text-red-400',
  },
  unknown: {
    icon: HelpCircle,
    row: 'border-border bg-muted/40',
    text: 'text-muted-foreground',
  },
};

/**
 * Used only when the API sends no `message`. `success` has no note at all —
 * a message field that isn't there must not be rendered as an error.
 */
const TONE_NOTE: Record<TKbSyncTone, string> = {
  success: '',
  warning: 'No text found — the page is client-rendered.',
  error: '',
  unknown: 'Unrecognised status — not counted as a failure.',
};

interface KbSyncResultsProps {
  results: IKbSyncResult[];
  /** Set when the sync itself failed; `results` is empty in that case. */
  errorMessage?: string | null;
  onDismiss: () => void;
}

const KbSyncResults = ({
  results,
  errorMessage,
  onDismiss,
}: KbSyncResultsProps) => {
  const counts = results.reduce(
    (acc, result) => {
      acc[kbSyncStatusTone(result.status)] += 1;
      return acc;
    },
    { success: 0, warning: 0, error: 0, unknown: 0 },
  );

  return (
    <section
      aria-label="Sync results"
      className="rounded-lg border bg-card p-4 mb-4"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-sm font-semibold">Last sync</h3>

          {errorMessage ? (
            <p className="text-sm text-destructive">{errorMessage}</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              <span className={TONE_STYLES.success.text}>
                {counts.success} indexed
              </span>
              {' · '}
              <span className={TONE_STYLES.warning.text}>
                {counts.warning} empty
              </span>
              {' · '}
              <span className={TONE_STYLES.error.text}>
                {counts.error} error
              </span>
              {counts.unknown > 0 && (
                <>
                  {' · '}
                  <span className={TONE_STYLES.unknown.text}>
                    {counts.unknown} unknown
                  </span>
                </>
              )}
            </p>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          aria-label="Dismiss sync results"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2">
          <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
          <p className="text-sm text-muted-foreground">
            The sync did not run, so no sources were checked.
          </p>
        </div>
      )}

      {!errorMessage && (
        <ul className="space-y-2">
          {results.map((result, index) => {
            const tone = kbSyncStatusTone(result.status);
            const { icon: Icon, row, text } = TONE_STYLES[tone];
            const note = result.message || TONE_NOTE[tone];

            return (
              <li
                key={`${result.url}-${index}`}
                className={cn(
                  'flex items-start gap-3 rounded-md border px-3 py-2',
                  row,
                )}
              >
                <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', text)} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium break-all">{result.url}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className={cn('font-semibold uppercase', text)}>
                      {result.status}
                    </span>
                    {typeof result.chars === 'number' && (
                      <> · {result.chars.toLocaleString()} chars</>
                    )}
                    {note && <> · {note}</>}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default KbSyncResults;
